/* eslint-disable react/jsx-no-bind */
import { useMemo } from "react";
import { useFilterStore, useStateCounts } from "~/components/build/Show/lib/useFilterStore";
import {
  FilterByOption,
  FILTER_STATE_CONFIG,
  StateOrder,
} from "~/components/build/Show/lib/filterConstants";
import { FilterStateIcon } from "~/components/build/Show/lib/FilterStateIcon";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import {
  FilterMenu,
  type FilterCategory,
  type SelectedFilter,
} from "~/components/build/Show/components/Filter";

export const STATE_FILTER_CATEGORY_KEY = "state";

/**
 * Build the single "state" `FilterCategory` consumed by both `FilterByMenu`
 * (the dropdown trigger in the new docked-annotations layout) and
 * `FilterByChips` (the chip strip alongside it).
 *
 * Exported so the chip component can derive the same category shape without
 * duplicating the option / hidden / count wiring.
 */
export function useFilterCategories(): FilterCategory[] {
  const counts = useStateCounts();
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);

  return useMemo(() => {
    const visibleFilters = showSkippedSteps
      ? StateOrder
      : StateOrder.filter((filter) => filter !== FilterByOption.Ignored);

    return [
      {
        key: STATE_FILTER_CATEGORY_KEY,
        label: "State",
        noun: "state",
        pluralNoun: "states",
        options: visibleFilters.map((filter) => ({
          value: filter,
          label: FILTER_STATE_CONFIG[filter].label,
          icon: <FilterStateIcon filter={filter} />,
          count: counts[filter],
          // Mirrors the previous "N others with no results" affordance: states
          // with zero matching steps are tucked away by default. Per-chip
          // submenus inherit this — the dropdown the chip opens shows the same
          // hidden-options toggle.
          hidden: counts[filter] === 0,
        })),
      },
    ];
  }, [counts, showSkippedSteps]);
}

function selectedToFilterBy(selected: SelectedFilter[]): FilterByOption[] {
  return selected
    .filter((filter) => filter.categoryKey === STATE_FILTER_CATEGORY_KEY)
    .map((filter) => filter.value as FilterByOption);
}

function filterByToSelected(filterBy: FilterByOption[]): SelectedFilter[] {
  return filterBy.map((value) => ({ categoryKey: STATE_FILTER_CATEGORY_KEY, value }));
}

/**
 * Modern docked-annotations replacement for `FilterByDropdown`. Renders the
 * generic `FilterMenu` driven by the shared `useFilterCategories()` hook
 * so it stays in lock-step with the chip strip.
 */
export default function FilterByMenu() {
  const filterBy = useFilterStore((state) => state.filterBy);
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const track = useTrack();
  const categories = useFilterCategories();

  const selected = useMemo(() => filterByToSelected(filterBy), [filterBy]);

  function handleChange(next: SelectedFilter[]) {
    const newFilterBy = selectedToFilterBy(next);
    setFilterBy(newFilterBy);
    track("Change State Filter", { enabled: newFilterBy });
  }

  return (
    <FilterMenu
      testId="filter-by-dropdown"
      categories={categories}
      selected={selected}
      onChange={handleChange}
    />
  );
}
